// src/pages/equipmentrentaladmin/equipment/AddEquipment.jsx

import React, { useState, useContext, useRef, useEffect } from "react";
import { FaPlus, FaArrowLeft } from "react-icons/fa";
import { FiTag, FiCheckCircle, FiBriefcase } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import RentalAdminLayout from "../../../../layout/equipmentrentaladmin/RentalAdminLayout.js";
import AppContext from "../../../../context/AppContext.js";
import axios from "axios";
import "../../../../assets/css/equipmentrentaladmin/myequipment/MyEquipment.css";

const AddEquipment = () => {
  const {
    baseUrl,
    baseUrlForFile,
    loggedInRental,
    isRentalVerified,
    authToken
  } = useContext(AppContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // ── Form States ──
  const [equipmentname, setEquipmentName] = useState("");
  const [category_id, setCategory_id] = useState("");
  const [ratePerHour, setRatePerHour] = useState("");
  const [ratePerDay, setRatePerDay] = useState("");
  const [ratePerAcre, setRatePerAcre] = useState("");
  const [status, setStatus] = useState("");
  const [about, setAbout] = useState("");
  const [image, setImage] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [specTags, setSpecTags] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);

  // ── Business Detail (read-only, comes from profile) ──
  const [businessDetail, setBusinessDetail] = useState(null);

  // ── KYC Modal State ──
  const [showKycModal, setShowKycModal] = useState(false);

  // ── Handlers ──
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setSelectedImage(URL.createObjectURL(file));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !specTags.includes(tagInput.trim())) {
      setSpecTags([...specTags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const removeTag = (tag) => setSpecTags(specTags.filter(t => t !== tag));

  // ── Fetch Categories ──
  const fetchAllCategories = () => {
    axios
      .get(`${baseUrl}/rentaladmin/category/fetchAllCategories`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          setCategories(response.data.categories);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

 // ── Fetch Business Detail — use existing fetchrentalProfile endpoint ──
const fetchBusinessDetail = () => {
  axios
    .get(`${baseUrl}/rentaladmin/profile/fetchrentalProfile`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
    .then((response) => {
      if (response.data.success === true && response.data.business) {
        setBusinessDetail(response.data.business);
      } else {
        setBusinessDetail(null);
      }
    })
    .catch((error) => {
      console.log(error);
      setBusinessDetail(null);
    });
};

  useEffect(() => {
    fetchAllCategories();
    fetchBusinessDetail();
  }, []);

  // ── Validation ──
  const validate = () => {
    const e = {};
    if (!equipmentname.trim()) e.equipmentname = "Equipment name is required";
    if (!category_id) e.category_id = "Category is required";
    if (!ratePerHour && !ratePerDay && !ratePerAcre) {
      e.rates = "Please enter at least one rate (hour, day, or acre)";
    }
    if (!status) e.status = "Status is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ──
  const handleSubmitClick = () => {
    if (!validate()) return;

    if (!isRentalVerified) {
      setShowKycModal(true);
      return;
    }

    if (!businessDetail) {
      alert("Please complete your business details first before adding equipment.");
      navigate("/rental-admin/businessdetail");
      return;
    }

    addEquipment();
  };

  const addEquipment = () => {
    setLoading(true);
    if (!loggedInRental) {
      alert("Rental Admin data not found. Please login again.");
      setLoading(false);
      return;
    }
    const formdata = new FormData();
    formdata.append("srrentaladmin_id", loggedInRental?.id);
    formdata.append("equipmentname", equipmentname);
    formdata.append("category_id", category_id);
    if (ratePerHour) formdata.append("rate_per_hour", ratePerHour);
    if (ratePerDay) formdata.append("rate_per_day", ratePerDay);
    if (ratePerAcre) formdata.append("rate_per_acre", ratePerAcre);
    formdata.append("status", status);
    formdata.append("specification", specTags.join(","));
    formdata.append("about", about);
    if (image) formdata.append("image", image);

    axios.post(
      `${baseUrl}/rentaladmin/equipmentmanagement/addequipment`,
      formdata,
      { headers: { "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${authToken}`
      } }
    )
      .then((response) => {
        setLoading(false);
        if (response.data.success) {
          navigate("/rental-admin/myequipment");
        } else if (response.data.kyc_required) {
          setShowKycModal(true);
        } else if (response.data.business_detail_required) {
          alert(response.data.message);
          navigate("/rental-admin/businessdetail");
        } else {
          alert(response.data.message);
        }
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
  };

  const kycBenefits = [
    "Add and manage equipment listings",
    "Receive rental requests from farmers",
    "Withdraw earnings to your account",
    "Build trust with your customers",
  ];

  return (
    <RentalAdminLayout>
      <div className="meq-page">

        <div className="meq-section-header" style={{ marginBottom: "24px" }}>
          <div>
            <button
              className="meq-cancel-btn"
              style={{ marginBottom: "8px", display: "flex", alignItems: "center", gap: "6px" }}
              onClick={() => navigate("/rental-admin/myequipment")}
            >
              <FaArrowLeft size={13} /> Back to Equipment List
            </button>
            <h2 className="meq-section-title">Onboard New Equipment</h2>
            <p style={{ fontSize: "13px", color: "#666", marginTop: "4px", fontFamily: "Poppins, sans-serif" }}>
              Fill in the details below to add a new equipment listing.
            </p>
          </div>
        </div>

        <div className="meq-table-section">
          <div style={{ padding: "28px" }}>

            {/* ── Business Info (read-only) ── */}
            <div
              style={{
                background: "#FFF8E7",
                border: "1px solid #FDB714",
                borderRadius: "8px",
                padding: "12px 16px",
                marginBottom: "24px",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "13px",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              <FiBriefcase size={18} color="#FDB714" />
              {businessDetail ? (
                <span>
                  Listing under: <b>{businessDetail.businessname}</b> — {businessDetail.address}
                </span>
              ) : (
                <span>
                  Business details not found. Please{" "}
                  <b
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => navigate("/rental-admin/businessdetail")}
                  >
                    complete your business profile
                  </b>{" "}
                  first.
                </span>
              )}
            </div>

            <div className="meq-form-grid">

              <div className="meq-upload-section">
                <label className="meq-form-label">Equipment Picture</label>
                <div className="meq-upload-box" onClick={() => fileInputRef.current.click()}>
                  {selectedImage
                    ? <img src={selectedImage} alt="Preview" className="meq-preview-img" />
                    : <span className="meq-upload-placeholder">UPLOAD PHOTO</span>}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                />
                <button className="meq-choose-btn" onClick={() => fileInputRef.current.click()}>
                  Choose File
                </button>
              </div>

              <div className="meq-form-fields">

                <div className="meq-field-row">
                  <div className="meq-field-group">
                    <label className="meq-form-label">Equipment Name *</label>
                    <input
                      className={`meq-input ${errors.equipmentname ? "meq-input-error" : ""}`}
                      type="text"
                      placeholder="e.g. John Deere 5050D"
                      value={equipmentname}
                      onChange={e => setEquipmentName(e.target.value)}
                    />
                    {errors.equipmentname && <span className="meq-error-text">{errors.equipmentname}</span>}
                  </div>
                  <div className="meq-field-group">
                    <label className="meq-form-label">Category *</label>
                    <select
                      className={`meq-input ${errors.category_id ? "meq-input-error" : ""}`}
                      value={category_id}
                      onChange={(e) => setCategory_id(e.target.value)}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    {errors.category_id && <span className="meq-error-text">{errors.category_id}</span>}

                    {/* Category select hote hi uska type (Self-Powered /
                        Implement) hamesha dikhega. */}
                    {(() => {
                      const selectedCategory = categories.find(
                        (cat) => String(cat.id) === String(category_id)
                      );
                      if (!selectedCategory) return null;

                      const isImplement = selectedCategory.equipment_type === "implement";

                      return (
                        <>
                          <p className="meq-category-type-label">
                            Type:{" "}
                            <strong>{isImplement ? "Implement" : "Self-Powered"}</strong>
                          </p>

                          {/* Implement wali category ke liye reminder —
                              koi specific equipment ka naam nahi liya,
                              generic wording rakhi hai. */}
                          {isImplement && (
                            <p className="meq-implement-hint">
                              ⚠️ This is an implement — it needs another
                              equipment to operate. Make sure your rental
                              rate includes the cost of that equipment,
                              driver and diesel.
                            </p>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* ── Rate Fields (Hour / Day / Acre) ── */}
                <div className="meq-field-group meq-full-width" style={{ marginBottom: "4px" }}>
                  <label className="meq-form-label">
                    Rental Rates <span style={{ fontWeight: 400, color: "#888", fontSize: "12px" }}>
                      (kam az kam ek rate zaroor dein)
                    </span>
                  </label>
                </div>
                <div className="meq-field-row">
                  <div className="meq-field-group">
                    <label className="meq-form-label">Rate /Hour (Rs)</label>
                    <input
                      className={`meq-input ${errors.rates ? "meq-input-error" : ""}`}
                      type="number"
                      placeholder="e.g. 500"
                      value={ratePerHour}
                      onChange={e => setRatePerHour(e.target.value)}
                    />
                  </div>
                  <div className="meq-field-group">
                    <label className="meq-form-label">Rate /Day (Rs)</label>
                    <input
                      className={`meq-input ${errors.rates ? "meq-input-error" : ""}`}
                      type="number"
                      placeholder="e.g. 3500"
                      value={ratePerDay}
                      onChange={e => setRatePerDay(e.target.value)}
                    />
                  </div>
                  <div className="meq-field-group">
                    <label className="meq-form-label">Rate /Acre (Rs)</label>
                    <input
                      className={`meq-input ${errors.rates ? "meq-input-error" : ""}`}
                      type="number"
                      placeholder="e.g. 1200"
                      value={ratePerAcre}
                      onChange={e => setRatePerAcre(e.target.value)}
                    />
                  </div>
                </div>
                {errors.rates && <span className="meq-error-text">{errors.rates}</span>}

                <div className="meq-field-row">
                  <div className="meq-field-group">
                    <label className="meq-form-label">Status *</label>
                    <select
                      className={`meq-input ${errors.status ? "meq-input-error" : ""}`}
                      value={status}
                      onChange={e => setStatus(e.target.value)}
                    >
                      <option value="">Select Status</option>
                      <option value="available">Available</option>
                      <option value="rented">Rented</option>
                      <option value="maintenance">Maintenance</option>
                    </select>
                    {errors.status && <span className="meq-error-text">{errors.status}</span>}
                  </div>
                </div>

                <div className="meq-field-group meq-full-width">
                  <label className="meq-form-label">About</label>
                  <textarea
                    className="meq-input meq-textarea"
                    placeholder="Write equipment details..."
                    value={about}
                    onChange={e => setAbout(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <p className="meq-section-label"><FiTag size={14} /> Specification Tags</p>
            <div className="meq-tag-input-row">
              <input
                className="meq-input"
                type="text"
                placeholder="Add specification tag..."
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && handleAddTag()}
              />
              <button className="meq-add-tag-btn" onClick={handleAddTag}><FaPlus /></button>
            </div>
            <div className="meq-tags-row">
              {specTags.map((tag, i) => (
                <span key={i} className="meq-tag-chip">
                  {tag} <span className="meq-tag-remove" onClick={() => removeTag(tag)}>×</span>
                </span>
              ))}
            </div>

            <div className="meq-form-buttons" style={{ marginTop: "32px" }}>
              <button
                className="meq-cancel-btn"
                onClick={() => navigate("/rental-admin/equipment")}
              >
                Cancel
              </button>
              <button
                className="meq-confirm-btn"
                onClick={handleSubmitClick}
                disabled={loading}
              >
                {loading ? "Adding..." : "Confirm Addition"}
              </button>
            </div>

          </div>
        </div>

        {showKycModal && (
          <div className="kyc-overlay" onClick={() => setShowKycModal(false)}>
            <div className="kyc-modal" onClick={e => e.stopPropagation()}>
              <button className="kyc-close-btn" onClick={() => setShowKycModal(false)}>✕</button>
              <div className="kyc-top">
                <div className="kyc-icon-wrapper">
                  <FiCheckCircle size={36} color="#fff" />
                </div>
                <h2 className="kyc-title">Complete Your Verification</h2>
                <p className="kyc-subtitle">
                  To add equipment on <b>AgriRent</b>, please complete your KYC verification first.
                </p>
                <div className="kyc-benefits-box">
                  <p className="kyc-benefits-title">Benefits of Verification:</p>
                  {kycBenefits.map((benefit, i) => (
                    <div key={i} className="kyc-benefit-item">
                      <div className="kyc-benefit-icon">✅</div>
                      <span className="kyc-benefit-text">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="kyc-bottom">
                <button
                  className="kyc-verify-btn"
                  onClick={() => { setShowKycModal(false); navigate("/rental-admin/profile"); }}
                >
                  Verify Now
                </button>
                <button className="kyc-skip-btn" onClick={() => setShowKycModal(false)}>
                  Skip for Now
                </button>
                <p className="kyc-note">
                  You can complete verification anytime from Settings → Documents
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </RentalAdminLayout>
  );
};

export default AddEquipment;